/**
 * If The SessionRewind `<script>` has been installed, which sets `window.sessionRewind`,
 * then store events in a growing session "attributes" key-value object. Otherwise do nothing.
 *
 * Documentation: https://dash.sessionrewind.com/account-settings/sdk
 * NOTE: Events are not actually supported. What is supported is custom
 * user and session attributes (key-value store). We are trialing "snowballing"
 * events into a growing attributes object. The object must be merged onto, as
 * the new object completely replaces all prior values. Values must be strings.
 */
export function trackEventViaSessionRewind (name, data) {
  if (window.sessionRewind) {
    try {
      const sessionInfo = window.sessionRewind.getSessionInfo() || {}
      sessionInfo[name] ||= flattenObjectToString(data)
      window.sessionRewind.setSessionInfo(sessionInfo)
    } catch (e) {
      // eslint-disable-next-line no-undef
      Rollbar.error('Failed to track event via SessionRewind', e)
    }
  }
}

/**
 * If The SessionRewind `<script>` has been installed, which sets `window.sessionRewind`,
 * then append attributes to the current User attributes object. Otherwise do nothing.
 *
 * Documentation: https://dash.sessionrewind.com/account-settings/sdk
 * NOTE: all user attributes must be sent as strings.
 */
export function identifyUserViaSessionRewind (userId, userAttributes = {}) {
  if (window.sessionRewind) {
    try {
      const payload = { ...(window.sessionRewind.getUserInfo() || {}) }

      if (userId) {
        payload.userId = String(userId)
      } else if (!payload.userId) {
        payload.userId = null
      }

      _.forEach(userAttributes, (value, key) => {
        payload[key] = String(value)
      })

      window.sessionRewind.identifyUser(payload)
    } catch (e) {
      // eslint-disable-next-line no-undef
      Rollbar.error('Failed to identify user via SessionRewind', e)
    }
  }
}

/**
 * Recursively flattens a key-value pair object, which can contain nested objects
 * and arrays, to a delmited string. Keys and values are delimited by colon (`:`),
 * and key-value pairs are delimited by pipe (`|`). Object properties and array indices,
 * are included in the keys, delimited by dot (`.`). For example:
 * ```js
 * flattenObjectToString({
 *  hello: { text: 'world', isExample: true },
 *  second: 2,
 *  numbers: [9,8,7]
 * })
 * // => "hello.text:world|hello.isExample:true|second:2|numbers.0:9|numbers.1:8|numbers.2:7"
 * ```
 */
const flattenObjectToString = (obj) => {
  let flatString = ''

  const recurse = (curr, prefix) => {
    if (_.isArray(curr)) {
      _.forEach(curr, (value, index) => {
        const newPrefix = `${prefix}.${index}`
        recurse(value, newPrefix)
      })
    } else if (_.isObject(curr)) {
      _.forEach(curr, (value, key) => {
        const newPrefix = prefix ? `${prefix}.${key}` : key
        recurse(value, newPrefix)
      })
    } else {
      flatString += `${prefix}:${curr}|`
    }
  }

  recurse(obj, '')

  return _.trimEnd(flatString, '|')
}
